/** @forge-header
 * @id          soccerParser
 * @role        Soccer ticker parser | KXUECLGAME / KXWCGAME / KXINTLFRIENDLYGAME 3-way game moneylines
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/helpers:parseFallback,splitAnchoredCompetitors
 * @downstream  kalshiTicker/parse
 * @exports     parseSoccerGame:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo, PlayerInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, splitAnchoredCompetitors, stripTimePrefix, isDrawOutcome, formatPlayerName, noteSoccerPair, soccerPairOf } from '../helpers';
import { SOCCER_LEAGUE_NAMES } from './soccerLeagueNames';

/**
 * Soccer: KXUECLGAME-26MAY27CRYRVC-CRY | KXWCGAME-26JUN27JORARG-TIE
 * | KXINTLFRIENDLYGAME-26JUN08FRAIRL-FRA
 * 3-way (Home / Away / Draw) — one ticker per outcome. The marketStore
 * multi-outcome builder groups the three tickers by event; this lib parser
 * exists for display (TickerDisplay chip + describeMarket label on positions).
 *
 * The Draw ticker's outcome bounds neither team, so it leaves the matchup
 * UNRESOLVED (#837) — the label falls back to the exact ticker plus "Draw"
 * rather than to a midpoint guess that disagrees with the two sibling markets
 * of the same event. describeMarket recovers the pair from hydra's canonical
 * away/home when a frame is in hand, which for soccer it reliably is.
 */
export function parseSoccerGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const series = parts[0] ?? 'KXSOCCER';
  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null;

  // A team ticket PROVES the split and teaches it to the match's BTTS legs; it never reads one back — a draw leg's
  // pair stays the producer's to supply (#837).
  const pair = splitAnchoredCompetitors(mid.slice(7), outcome);
  if (pair) noteSoccerPair(mid, pair[0], pair[1]);
  const game: GameInfo | null = pair ? { awayCode: pair[0], homeCode: pair[1] } : null;

  const outcomeLabel = isDrawOutcome(outcome) ? 'Draw' : outcome ? `${outcome} Win` : null;
  const matchup = game ? `${game.awayCode} v ${game.homeCode}` : raw;
  const shortLabel = `Soccer: ${matchup}`;
  const fullLabel = `Soccer: ${matchup}${outcomeLabel ? ` · ${outcomeLabel}` : ''}`;

  return {
    raw,
    category: 'soccer-game',
    series,
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

/**
 * Soccer knockout "To Advance": KXWCADVANCE-26JUL01ENGCOD-ENG |
 * KXUECLADVANCE-…. A 2-way (the two teams, NO draw) settled on who advances
 * incl. extra time + penalties. One ticker per team — the outcome is always a
 * team code (never TIE), so it splits the team chunk like a moneyline — and
 * leaves the matchup unresolved on the (unobserved) shape that carries no
 * outcome at all, rather than midpoint-guessing it (#837).
 * Reuses the 'soccer-game' category (no new color-map entry); the
 * 'KXSOCCERADVANCE' series marker lets describeMarket tag the bet team as
 * "<TEAM> Adv" so a held position reads apart from the regulation 1X2 winner.
 */
export function parseSoccerAdvance(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null; // a team code (never a draw)

  // A team ticket PROVES the split and teaches it to the match's BTTS legs; it never reads one back — a draw leg's
  // pair stays the producer's to supply (#837).
  const pair = splitAnchoredCompetitors(mid.slice(7), outcome);
  if (pair) noteSoccerPair(mid, pair[0], pair[1]);
  const game: GameInfo | null = pair ? { awayCode: pair[0], homeCode: pair[1] } : null;

  const advLabel = outcome ? `${outcome} Advances` : 'To Advance';
  const matchup = game ? `${game.awayCode} v ${game.homeCode}` : raw;
  const shortLabel = `Soccer: ${matchup} · To Advance`;
  const fullLabel = `Soccer: ${matchup} · ${advLabel}`;

  return {
    raw,
    category: 'soccer-game',
    series: 'KXSOCCERADVANCE',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

/**
 * Soccer BTTS (both teams to score): KXWCBTTS-26JUN18SUIBIH-BTTS |
 * KXWC1HBTTS-… | KXWC2HBTTS-…. A single binary per game/period — no full-game
 * spread/total/game equivalent, so it parses directly (parse.ts dispatches the
 * BTTS prefix here). Outcome is always 'BTTS'; the period (if any) annotates the
 * side label. BTTS names no competitor at all — there has never been an
 * outcome here to bound the packed pair — so the matchup is left UNRESOLVED
 * and the label carries the exact ticker plus "Both Teams To Score" (#837).
 * describeMarket restores the matchup from hydra's canonical away/home, which
 * is stamped per fixture and so covers BTTS like every other family.
 * Reuses the 'soccer-game' category to avoid a new color-map entry.
 */
export function parseSoccerBtts(
  raw: string,
  rest: string,
  period?: ParsedTicker['period'],
): ParsedTicker {
  const mid = (rest.split('-')[0] ?? '');
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));

  const periodLabel = period === '1H' ? ' · 1st Half' : period === '2H' ? ' · 2nd Half' : '';
  // BTTS names no team, so the split comes only from a team ticker or game key of the same match (soccerPairOf).
  const pair = soccerPairOf(mid);
  const game: GameInfo | null = pair ? { awayCode: pair[0], homeCode: pair[1] } : null;
  const matchup = game ? `${game.awayCode} v ${game.homeCode}` : raw;
  const shortLabel = `Soccer: ${matchup} · BTTS`;
  const fullLabel = `Soccer: ${matchup} · Both Teams To Score${periodLabel}`;

  return {
    raw,
    category: 'soccer-game',
    series: 'KXSOCCERBTTS',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: 'BTTS',
    period,
    shortLabel,
    fullLabel,
  };
}

/** Kalshi soccer league prefixes the parsers recognise (2026-09-09: the
 *  three big-five leagues joined the plane alongside EPL/MLS/UEL/UCL/WC). */
export const SOCCER_LEAGUE_PREFIXES = [
  'WC', 'UECL', 'INTLFRIENDLY', 'EPL', 'MLS', 'UEL', 'UCL', 'SERIEA', 'BUNDESLIGA', 'LALIGA',
  // 2026-09-25: Liga MX, Liga de Expansión, CONCACAF / UEFA Nations League, Dimayor (Colombia).
  'LIGAMX', 'LIGAEXP', 'CONCACAFNL', 'UEFANL', 'DIMAYOR',
] as const;

/** The first-goalscorer field's "no goal" leg (`KXEPLFIRSTGOAL-…-NOGOAL`). */
export const SOCCER_GOAL_NONE_KEY = 'NOGOAL';

/** `<TEAM><INITIAL><SURNAME><JERSEY?>` → the team is one END of the packed
 *  pair (Kalshi writes the player's club code first: `ARSNMADUE20` on the
 *  ARSCFC fixture). Tries both ends, 3-letter codes first (the live slates'
 *  shape), then 4 and 2. Null when neither end matches — the NOGOAL leg. */
function splitSoccerPlayerSegment(
  seg: string,
  pair: string,
): { teamCode: string; awayCode: string; homeCode: string; rest: string } | null {
  for (const len of [3, 4, 2]) {
    if (pair.length - len < 2) continue;
    const head = pair.slice(0, len);
    if (seg.startsWith(head) && seg.length > len) {
      return { teamCode: head, awayCode: head, homeCode: pair.slice(len), rest: seg.slice(len) };
    }
    const tail = pair.slice(-len);
    if (seg.startsWith(tail) && seg.length > len) {
      return { teamCode: tail, awayCode: pair.slice(0, -len), homeCode: tail, rest: seg.slice(len) };
    }
  }
  return null;
}

/**
 * Soccer goalscorer props (soccer-goalscorer-props, 2026-09-09):
 *   KXEPLGOAL-26SEP06ARSCFC-ARSNMADUE20-1        goals ladder, rung "1+"
 *   KXEPLFIRSTGOAL-26SEP06ARSCFC-CFCWFOFAN29     first goalscorer, one leg per player
 *   KXEPLFIRSTGOAL-26SEP06ARSCFC-NOGOAL          first goalscorer, the no-goal leg
 * Same anatomy as the NFL player props (team code + initial + surname +
 * jersey); the wire `player` / `playerTeam` (core-stamped) are preferred by
 * the store — this parser is the display / positions fallback.
 */
export function parseSoccerGoal(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 3) return parseFallback(raw);
  const series = parts[0] ?? '';
  const first = series.endsWith('FIRSTGOAL');
  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw);
  const date = parseDateFromTicker(mid.slice(0, 7));
  const pair = stripTimePrefix(mid.slice(7));
  const seg = parts[2] ?? '';
  const threshold = !first && /^\d+$/.test(parts[3] ?? '') ? Number(parts[3]) : null;
  const isNone = seg === SOCCER_GOAL_NONE_KEY;
  const split = isNone ? null : splitSoccerPlayerSegment(seg, pair);
  let awayCode: string;
  let homeCode: string;
  let player: PlayerInfo | null = null;
  if (split) {
    awayCode = split.awayCode;
    homeCode = split.homeCode;
    const m = split.rest.match(/^([A-Z]+)(\d+)?$/);
    if (m) {
      const jerseyNumber = m[2] ? Number(m[2]) : undefined;
      player = {
        teamCode: split.teamCode,
        playerId: m[1],
        jerseyNumber,
        displayName: formatPlayerName(m[1], jerseyNumber),
      };
    }
  } else {
    const half = Math.floor(pair.length / 2);
    awayCode = pair.slice(0, half);
    homeCode = pair.slice(half);
  }
  const game: GameInfo = { awayCode, homeCode };
  const who = player?.displayName ?? (isNone ? 'No Goal' : seg);
  const outcome = first ? (isNone ? SOCCER_GOAL_NONE_KEY : seg) : threshold != null ? `${threshold}+` : null;
  const kind = first ? '1st Goal' : threshold != null ? `${threshold}+ Goals` : 'Goals';
  const shortLabel = `Soccer: ${awayCode} v ${homeCode} · ${first ? '1st Goal' : 'Goals'}`;
  const fullLabel = `Soccer: ${awayCode} v ${homeCode} · ${isNone ? 'No Goal' : `${who} ${kind}`}`;
  return {
    raw,
    category: first ? 'soccer-first-goal' : 'soccer-goal',
    series,
    date,
    game,
    player,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

// ── Goalscorer prop vocabulary (soccer-goalscorer-props, 2026-09-09) ───────
// Mirrors the NFL shape (sports/nfl.ts): stat ids match the core's wire
// `stat` on `class: 'player-prop'` rows (hydracore manifest/derive.ts:
// `goal` for KX<LEAGUE>GOAL, `first-goal` for KX<LEAGUE>FIRSTGOAL).

export type SoccerPropStat = 'goal' | 'first-goal';
export const SOCCER_PROP_STATS: readonly SoccerPropStat[] = ['goal', 'first-goal'];
export const SOCCER_PROP_STAT_LABELS: Record<SoccerPropStat, string> = {
  goal: 'Goals',
  'first-goal': 'First Goalscorer',
};
/** first-goal is a field of per-player binaries (plus NOGOAL): one rung at a
 *  fixed identity threshold, like the NFL touchdown binaries. */
export const SOCCER_PROP_BINARY_THRESHOLD: Partial<Record<SoccerPropStat, number>> = {
  'first-goal': 1,
};
export const SOCCER_PROP_NONE_PLAYER_KEY = SOCCER_GOAL_NONE_KEY;
export const SOCCER_PROP_NONE_PLAYER_NAME = 'No Goal';

const SOCCER_PROP_SERIES_RE = new RegExp(`^KX(?:${SOCCER_LEAGUE_PREFIXES.join('|')})(FIRSTGOAL|GOAL)$`);

export function isSoccerPropSeriesTicker(ticker: string): boolean {
  return SOCCER_PROP_SERIES_RE.test(ticker.split('-')[0] ?? '');
}

export interface SoccerPropClass {
  kind: 'prop';
  stat: SoccerPropStat;
  date: string | null;
  awayCode: string | null;
  homeCode: string | null;
  leg: 'player' | 'none';
  /** '' on the NOGOAL leg. */
  teamCode: string;
  playerId: string;
  jerseyNumber?: number;
  /** Ladder rung on `goal`; null on the first-goal binaries. */
  threshold: number | null;
}

/** Structural classification of a goalscorer ticker (store-side identity
 *  source for the player segment; the wire class selects the row). Null for
 *  anything that is not a goal series or does not match the anatomy. */
export function classifySoccerPropTicker(ticker: string): SoccerPropClass | null {
  const raw = ticker.trim().toUpperCase();
  const parts = raw.split('-');
  const m = (parts[0] ?? '').match(SOCCER_PROP_SERIES_RE);
  if (!m) return null;
  const stat: SoccerPropStat = m[1] === 'FIRSTGOAL' ? 'first-goal' : 'goal';
  const mid = parts[1] ?? '';
  if (mid.length < 9) return null;
  const date = parseDateFromTicker(mid.slice(0, 7));
  const pair = stripTimePrefix(mid.slice(7));
  const seg = parts[2] ?? '';
  if (!seg) return null;
  const half = Math.floor(pair.length / 2);
  if (stat === 'goal') {
    if (parts.length !== 4) return null;
    const threshold = /^\d+$/.test(parts[3] ?? '') ? Number(parts[3]) : NaN;
    if (!Number.isFinite(threshold)) return null;
    const split = splitSoccerPlayerSegment(seg, pair);
    if (!split) return null;
    const pm = split.rest.match(/^([A-Z]+)(\d+)?$/);
    if (!pm) return null;
    return {
      kind: 'prop', stat, date, awayCode: split.awayCode, homeCode: split.homeCode, leg: 'player',
      teamCode: split.teamCode, playerId: pm[1], jerseyNumber: pm[2] ? Number(pm[2]) : undefined, threshold,
    };
  }
  if (parts.length !== 3) return null;
  if (seg === SOCCER_PROP_NONE_PLAYER_KEY) {
    return {
      kind: 'prop', stat, date, awayCode: pair.slice(0, half), homeCode: pair.slice(half), leg: 'none',
      teamCode: '', playerId: SOCCER_PROP_NONE_PLAYER_KEY, threshold: null,
    };
  }
  const split = splitSoccerPlayerSegment(seg, pair);
  if (!split) return null;
  const pm = split.rest.match(/^([A-Z]+)(\d+)?$/);
  if (!pm) return null;
  return {
    kind: 'prop', stat, date, awayCode: split.awayCode, homeCode: split.homeCode, leg: 'player',
    teamCode: split.teamCode, playerId: pm[1], jerseyNumber: pm[2] ? Number(pm[2]) : undefined, threshold: null,
  };
}

/** The rung without the player — "1+ Goals", "First Goalscorer". */
export function soccerPropRungLabel(stat: SoccerPropStat, threshold: number): string {
  return stat === 'goal' ? `${threshold}+ Goals` : SOCCER_PROP_STAT_LABELS[stat];
}

/** The two sides of one rung (#251 wording): YES names the outcome, NO is
 *  its negation in plain words. */
export function soccerPropRungLabels(
  playerName: string,
  teamCode: string | null | undefined,
  stat: SoccerPropStat,
  threshold: number,
  playerKey?: string,
): { label: string; notLabel: string } {
  const team = teamCode ? ` (${teamCode})` : '';
  const who = `${playerName}${team}`;
  if (playerKey === SOCCER_PROP_NONE_PLAYER_KEY) {
    return { label: SOCCER_PROP_NONE_PLAYER_NAME, notLabel: 'A goal is scored' };
  }
  if (stat === 'first-goal') {
    return { label: `${who} scores first`, notLabel: `${who} does not score first` };
  }
  return { label: `${who} ${threshold}+ goals`, notLabel: `${who} under ${threshold} goal${threshold === 1 ? '' : 's'}` };
}

/** Competition label for a Kalshi soccer ticker / series / event ticker
 *  (`KXSERIEAGAME-26SEP20ACMLEC` → "Serie A"). Null when the prefix is not a
 *  registered league — callers omit the badge rather than guess. */
const SOCCER_LEAGUE_LABELS: Record<(typeof SOCCER_LEAGUE_PREFIXES)[number], string> = {
  WC: 'World Cup',
  UECL: 'UECL',
  INTLFRIENDLY: 'Friendly',
  EPL: 'EPL',
  MLS: 'MLS',
  UEL: 'UEL',
  UCL: 'UCL',
  SERIEA: 'Serie A',
  BUNDESLIGA: 'Bundesliga',
  LALIGA: 'La Liga',
  LIGAMX: 'Liga MX',
  LIGAEXP: 'Liga Expansión',
  CONCACAFNL: 'CONCACAF NL',
  UEFANL: 'UEFA NL',
  DIMAYOR: 'Dimayor',
};
const SOCCER_LEAGUE_RE = new RegExp(`^KX(${[...SOCCER_LEAGUE_PREFIXES].sort((a, b) => b.length - a.length).join('|')})[A-Z0-9]*(?:-|$)`);

/** Every other soccer root (2026-10-26), longest first so LIGUE1 never reads as a shorter root. */
const SOCCER_LEAGUE_NAME_RE = new RegExp(`^KX(${Object.keys(SOCCER_LEAGUE_NAMES).sort((a, b) => b.length - a.length).join('|')})[A-Z0-9]*(?:-|$)`);

export function soccerLeagueLabel(tickerOrSeries: string): string | null {
  const t = tickerOrSeries.toUpperCase();
  const m = t.match(SOCCER_LEAGUE_RE);
  if (m) return SOCCER_LEAGUE_LABELS[m[1] as (typeof SOCCER_LEAGUE_PREFIXES)[number]] ?? null;
  const n = t.match(SOCCER_LEAGUE_NAME_RE);
  return n ? (SOCCER_LEAGUE_NAMES[n[1]!] ?? null) : null;
}
